import { useState } from "react";
import { Zap, MapPin, Phone, PhoneCall, UserCheck, Ban } from "lucide-react";

// 24/7 Powerup Electrical — Balham/Wandsworth · sole-trader 24/7 callout, industrial hazard-amber
// Brief built in as 4 distinct sections: 1) fixed-price calculator, the page's defining section
// 2) full-width live availability strip 3) "how it's different" trust row 4) plain coverage list
const JOBS = [
  { name: "Consumer unit / fuse board upgrade", price: "£420" },
  { name: "Bathroom fan install", price: "£150" },
  { name: "Fault-finding & diagnostics", price: "£75" },
  { name: "Emergency callout", price: "£85" },
];

export default function PowerupAfter({ b }) {
  const [job, setJob] = useState(JOBS[0]);
  const waNumber = (b.phone || "").replace(/\D/g, "").replace(/^0/, "44");

  return (
    <div className="bg-black text-white">
      {/* Nav */}
      <div className="px-6 md:px-10 py-4 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2 font-anton text-xl tracking-wider">
          <Zap className="w-5 h-5 text-[#FFC107]" /> POWERUP<span className="text-[#FFC107]">.</span>
        </div>
        <nav className="hidden md:flex gap-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
          <span>Pricing</span><span>Coverage</span><span>Call</span>
        </nav>
        <a href={`tel:${b.phone}`} className="bg-[#FFC107] text-black px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold">Call now</a>
      </div>

      {/* Hero — sole-trader, no dispatcher, no van fleet */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/14319099/pexels-photo-14319099.jpeg)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-transparent" />
        <div className="relative px-6 md:px-10 py-20 md:py-28">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFC107] mb-6">BALHAM · WANDSWORTH · 24/7 CALLOUT</p>
          <h1 className="font-anton text-6xl md:text-8xl leading-[0.9] tracking-tight uppercase">
            No call centre.<br/>
            No van fleet.<br/>
            <span className="text-[#FFC107]">Just the number that answers.</span>
          </h1>
          <p className="mt-8 max-w-lg text-lg text-white/80">
            One electrician, on call around the clock across Balham, Clapham, Tooting and Streatham —
            {" "}{b.rating}★ across {b.reviews} jobs, priced before anyone picks up a screwdriver.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href={`tel:${b.phone}`} className="bg-[#FFC107] text-black px-8 py-4 font-anton text-xl uppercase tracking-wider hover:bg-white transition-colors">
              Call {b.phone}
            </a>
            <a href={`https://wa.me/${waNumber}`} className="border border-white text-white px-8 py-4 font-anton text-xl uppercase tracking-wider hover:border-[#FFC107] hover:text-[#FFC107] transition-colors">
              WhatsApp instead
            </a>
          </div>
        </div>
      </div>

      {/* Availability strip — the "live status" bullet, built as a real section */}
      <div className="bg-[#FFC107] text-black px-6 md:px-10 py-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 font-anton text-xl md:text-2xl uppercase tracking-tight">
          <span className="w-2.5 h-2.5 rounded-full bg-black animate-pulse" /> Available now — Balham &amp; surrounding SW postcodes
        </div>
        <span className="font-mono text-xs uppercase tracking-[0.3em]">24/7 · No booking app · No hold music</span>
      </div>

      {/* Fixed-price calculator — the page's defining section, prices shown up front */}
      <div className="border-y border-white/10 bg-[#0a0a0a] px-6 md:px-10 py-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFC107] mb-2">NO SURPRISE QUOTES</p>
        <h2 className="font-anton text-3xl md:text-4xl uppercase mb-10">Priced before he touches a wire.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          <div className="space-y-2">
            {JOBS.map((j) => (
              <button
                key={j.name}
                onClick={() => setJob(j)}
                className={`w-full text-left px-5 py-4 border font-mono text-sm uppercase tracking-wide transition-colors ${
                  job.name === j.name ? "border-[#FFC107] bg-[#FFC107]/10 text-[#FFC107]" : "border-white/15 text-white/70 hover:border-white/40"
                }`}
              >
                {j.name}
              </button>
            ))}
          </div>
          <div className="border border-[#FFC107] p-8">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-3">Fixed price for</p>
            <div className="font-anton text-2xl uppercase leading-tight mb-4">{job.name}</div>
            <div className="font-anton text-5xl text-[#FFC107]">{job.price}</div>
            <p className="mt-4 text-sm text-white/60">Quoted up front. What you're told is what you pay.</p>
          </div>
        </div>
      </div>

      {/* How it's different — the single-direct-line bullet, built as its own trust row */}
      <div className="px-6 md:px-10 py-16 bg-[#0a0a0a] border-y border-white/10">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFC107] mb-10">HOW IT'S DIFFERENT</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <PhoneCall className="w-6 h-6 text-[#FFC107] mb-3" />
            <h3 className="font-anton text-2xl uppercase mb-2">No call centre</h3>
            <p className="text-sm text-white/60">You speak to the electrician doing the job — not a dispatcher taking a message.</p>
          </div>
          <div>
            <UserCheck className="w-6 h-6 text-[#FFC107] mb-3" />
            <h3 className="font-anton text-2xl uppercase mb-2">One point of contact</h3>
            <p className="text-sm text-white/60">Same person quotes it, turns up, and does the work — no handoffs.</p>
          </div>
          <div>
            <Ban className="w-6 h-6 text-[#FFC107] mb-3" />
            <h3 className="font-anton text-2xl uppercase mb-2">No van fleet</h3>
            <p className="text-sm text-white/60">One van covering Balham and the surrounding postcodes, not a subcontracted callout.</p>
          </div>
        </div>
      </div>

      {/* Coverage — stated plainly, not buried in a dropdown */}
      <div className="px-6 md:px-10 py-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFC107] mb-6">WHERE HE COVERS</p>
        <div className="flex flex-wrap gap-4 font-anton text-3xl md:text-4xl uppercase text-white/90">
          {["Balham", "Clapham", "Tooting", "Streatham"].map((area, i) => (
            <span key={area} className="flex items-center">
              {area}
              {i < 3 && <span className="text-[#FFC107] mx-4">/</span>}
            </span>
          ))}
        </div>
      </div>

      {/* Visit */}
      <div className="px-6 md:px-10 py-16 bg-[#0a0a0a] border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-10">
        <div>
          <h3 className="font-anton text-4xl md:text-5xl uppercase">Trip a fuse.<br/><span className="text-[#FFC107]">Save the number.</span></h3>
        </div>
        <div className="space-y-3 text-sm">
          <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#FFC107]" /> {b.address}</p>
          <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#FFC107]" /> {b.phone}</p>
          <p className="font-mono text-xs text-white/50 mt-4">⭐ {b.rating} · {b.reviews} Google reviews</p>
        </div>
      </div>
    </div>
  );
}
